import { useState, useCallback, useRef } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { CuentaFormNew, type CuentaFormValues } from './CuentaFormNew'
import type { CuentaConSaldo } from '@/services/cuentasService'

interface CuentaFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cuenta?: CuentaConSaldo | null
  onSubmit: (data: CuentaFormValues) => void
  isLoading?: boolean
  /** Lista de instituciones existentes para autocompletado */
  instituciones?: string[]
}

export function CuentaFormDialog({
  open,
  onOpenChange,
  cuenta,
  onSubmit,
  isLoading,
  instituciones = [],
}: CuentaFormDialogProps) {
  const [isFormDirty, setIsFormDirty] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const isEditing = !!cuenta

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty,
    onDiscard: () => onOpenChange(false),
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    onOpenChange(newOpen)
  }

  // Trigger submit desde el boton externo
  const handleExternalSubmit = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [])

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle>
              {isEditing ? 'Editar Cuenta' : 'Nueva Cuenta'}
            </SheetTitle>
            <SheetDescription>
              {isEditing
                ? 'Modifica los datos de la cuenta bancaria'
                : 'Agrega una nueva cuenta para registrar pagos'}
            </SheetDescription>
          </SheetHeader>

          {/* Contenido scrolleable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <CuentaFormNew
              ref={formRef}
              cuenta={cuenta}
              onSubmit={onSubmit}
              onDirtyChange={setIsFormDirty}
              isLoading={isLoading}
              hideButtons
              instituciones={instituciones}
            />
          </div>

          {/* Footer sticky con botones */}
          <SheetFooter className="px-6 py-4 border-t bg-background">
            <div className="flex justify-end gap-3 w-full">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleExternalSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear Cuenta'}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}
